import { Component, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDiagramProject, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';

export type PaletteTab = 'search' | 'graph';

/**
 * Le sélecteur « Recherche | Graphe », au pied des deux palettes. Double des
 * raccourcis Cmd+K / Cmd+Shift+K, pour le jour où l'un d'eux ne passe pas (un
 * navigateur, une extension ou le système qui le garde pour lui) : la bascule
 * doit rester possible à la souris.
 *
 * Il ne bascule rien lui-même : il signale l'onglet choisi, l'hôte fait ce
 * que ferait son raccourci.
 *
 * Mode `compact` : le LANCEUR épinglé au bas du menu latéral — un onglet
 * accroché au bord bas comme une poignée de tiroir, de la largeur que lui
 * donne l'hôte, boutons centrés, les deux
 * boutons en gris foncé ; l'icône plus grande que le raccourci ; aucun onglet
 * actif. Le nom complet
 * passe dans l'infobulle et dans `aria-label`, puisqu'il n'est plus écrit.
 */
@Component({
  selector: 'app-palette-switcher',
  standalone: true,
  imports: [FontAwesomeModule, NgClass],
  template: `
    <!-- Compact : un onglet accroché au bord bas — coins arrondis en haut seulement,
         contour sur trois côtés, aucun du côté du bord. -->
    <div
      class="flex shrink-0"
      [ngClass]="compact()
        ? 'w-full justify-center gap-0.5 rounded-t-lg border border-b-0 border-gray-200 bg-gray-50 px-1 pt-1 pb-1'
        : 'rounded-md border border-gray-200 p-0.5 bg-white'"
      role="group"
      aria-label="Palettes"
    >
      @for (tab of tabs; track tab.id) {
        <button
          type="button"
          tabindex="-1"
          (mousedown)="$event.preventDefault()"
          (click)="tab.id !== active() && switchTo.emit(tab.id)"
          [attr.aria-pressed]="active() === null ? null : tab.id === active()"
          [attr.aria-label]="compact() ? tab.name + ' (' + tab.keys + ')' : null"
          [title]="tab.id === active() ? '' : tab.hint"
          class="flex items-center gap-1.5 px-2 py-0.5 rounded transition-colors duration-150 focus:outline-none"
          [ngClass]="compact()
            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/70 cursor-pointer'
            : tab.id === active()
              ? 'bg-gray-100 text-gray-900 cursor-default'
              : 'text-gray-500 hover:text-primary-800 hover:bg-primary-50 cursor-pointer'"
        >
          <!-- Compact : l'icône porte le bouton, le raccourci n'est qu'un rappel. -->
          <fa-icon [icon]="tab.icon" class="leading-none" [ngClass]="compact() ? 'text-[14px]' : 'text-[12px]'" aria-hidden="true"></fa-icon>
          @if (!compact()) { <span>{{ tab.label }}</span> }
          <kbd class="font-sans" [ngClass]="compact() ? 'text-[10px]' : 'text-gray-400'">{{ tab.keys }}</kbd>
        </button>
      }
    </div>
  `,
})
export class PaletteSwitcherComponent {
  /** La palette ouverte ; `null` pour le lanceur, qui n'est dans aucune. */
  readonly active = input<PaletteTab | null>(null);
  readonly compact = input(false);
  /** Le graphe, depuis la recherche, se centre sur la ligne active — l'hôte le sait, pas le sélecteur. */
  readonly graphHint = input('Graphe du propriétaire');
  readonly switchTo = output<PaletteTab>();

  private readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  get tabs() {
    return [
      {
        id: 'search' as const,
        label: 'Recherche',
        name: 'Rechercher un bail, un propriétaire, un locataire ou un bien',
        icon: faMagnifyingGlass,
        keys: this.isMac ? '⌘K' : 'Ctrl+K',
        hint: this.compact()
          ? `Rechercher un bail, un propriétaire, un locataire ou un bien (${this.isMac ? '⌘K' : 'Ctrl+K'})`
          : 'Revenir à la recherche',
      },
      {
        id: 'graph' as const,
        label: 'Graphe',
        name: 'Graphe du propriétaire',
        icon: faDiagramProject,
        keys: this.isMac ? '⌘⇧K' : 'Ctrl+Shift+K',
        hint: this.compact()
          ? `Graphe du propriétaire de la fiche ouverte (${this.isMac ? '⌘⇧K' : 'Ctrl+Shift+K'})`
          : this.graphHint(),
      },
    ];
  }
}
